import { FloatingPortal } from '@floating-ui/react-dom-interactions'
import { PropertyValue } from 'api/clients/PropertyClient'
import { ReactComponent as AddIcon } from 'assets/icons/add16.svg'
import cx from 'classnames'
import { Popover } from 'components/Popover/Popover'
import { useUniqueNewColor } from 'hooks/useUniqueNewColor'
import { clamp } from 'lodash'
import React, {
  useCallback,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  PropsWithChildren,
} from 'react'
import { useKey, useMeasure, useShallowCompareEffect } from 'react-use'
import {
  SelectPropertyEventDetails,
  selectPropertyEventName,
} from 'screens/Finder/components/FileTree/events/selectProperty'
import { OptionEdit } from './OptionEdit'
import styles from './OptionSelect.module.scss'
import { Tag } from './Tag'
import isEqual from 'lodash/isEqual'
import { useOverlayHack } from 'hooks/useOverlayHack'
import { ButtonProps } from 'blaze-ui/Button/Types'
import { Button } from 'blaze-ui/Button'
import { ChevronDown } from 'blaze-ui/icons'

export enum OptionSelectVariant {
  TableView = 'table_view',
  Filter = 'filter',
}

export type OptionSelectProps = {
  availableOptions: PropertyValue[]
  onEditingStateChange?: (editingState: boolean) => void
  isEditable?: boolean
  canEditOptions?: boolean
  /**
   * If onCreated is not passed the UI will not allow the user to create a new option
   */
  onCreated?: (option: string, color?: string) => void
  onUpdated?: (propertyValue: PropertyValue) => void
  onDeleted?: (propertyValue: PropertyValue) => void
  variant?: OptionSelectVariant
  allowEmpty?: boolean
} & (
  | {
      mode: 'multiple'
      selected?: string[]
      onChange: (selected: string[]) => void
    }
  | {
      mode: 'single'
      selected?: string
      onChange: (selected: string | null) => void
    }
)
export type OptionSelectHandles = {
  focus: () => void
  setEditing: (editingState: boolean) => void
  toggleEditing: () => void
}

interface OptionButtonProps extends Pick<ButtonProps, 'isDisabled' | 'endIcon'> {
  optionVariant: OptionSelectVariant
  onPress: () => void
  options: PropertyValue[]
  tags: string[]
}

const OptionButton = forwardRef<HTMLButtonElement, PropsWithChildren<OptionButtonProps>>(
  ({ onPress, isDisabled, optionVariant, endIcon, children, tags, options }, fRef) => {
    if (optionVariant !== 'filter') {
      return (
        <button
          ref={fRef}
          className={cx({
            [styles.selectedTags]: optionVariant === OptionSelectVariant.TableView,
          })}
          onClick={onPress}
          data-testid="options-select-selected-tags"
        >
          {tags.map((tag, index) => (
            <Tag key={index} color={options.find((opt) => opt.value === tag)?.meta?.color ?? ''}>
              {tag}
            </Tag>
          ))}
        </button>
      )
    }
    return (
      <Button
        endIcon={endIcon}
        fullWidth
        ref={fRef}
        onPress={onPress}
        size="md"
        variant="tertiary"
        data-testid="options-select-selected-tags"
      >
        {optionVariant === OptionSelectVariant.Filter && tags.length === 0 && <>Add Value</>}
        <div className={styles.tagList}>
          {tags.map((tag, index) => (
            <Tag key={index} color={options.find((opt) => opt.value === tag)?.meta?.color ?? ''}>
              {tag}
            </Tag>
          ))}
        </div>
      </Button>
    )
  },
)

export const OptionSelect = forwardRef<OptionSelectHandles, OptionSelectProps>(
  (
    {
      mode,
      isEditable = true,
      canEditOptions = true,
      selected = [],
      availableOptions,
      onEditingStateChange,
      onCreated,
      onUpdated,
      onDeleted,
      onChange,
      variant = OptionSelectVariant.TableView,
      allowEmpty = true,
    },
    ref,
  ) => {
    const selectedInput = Array.isArray(selected) ? selected : [selected]
    const inputTags = selectedInput.filter((tag) => availableOptions.some(({ value }) => value === tag))
    const [input, setInput] = useState('')
    const [tags, setTags] = useState<string[]>(inputTags)
    const editableTagListRef = React.useRef<HTMLDivElement>(null)
    const editableFieldRef = React.useRef<HTMLInputElement>(null)
    const selectedTagsButtonRef = React.useRef<HTMLButtonElement>(null)
    const [inputSizerRef, { width: inputWidth }] = useMeasure<HTMLDivElement>()
    const [buttonRef, { width: buttonWidth, height: buttonHeight }] = useMeasure<HTMLButtonElement>()
    useEffect(() => {
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      buttonRef(selectedTagsButtonRef.current!)
    }, [buttonRef, selectedTagsButtonRef])
    const [isEditing, setIsEditing] = useState(false)
    const { newColor, resetColor } = useUniqueNewColor(availableOptions)
    const [availableValues, setAvailableValues] = useState<string[]>(availableOptions.map(({ value }) => value))

    const canRemove = allowEmpty || (mode === 'multiple' && tags.length > 1)

    const propagateChange = useCallback(
      (newTags: string[]) => {
        setTags(newTags)

        if (mode === 'single') {
          onChange(newTags[0] ?? null)
        } else {
          onChange(newTags)
        }
      },
      [mode, onChange],
    )

    useMemo(() => {
      if (!allowEmpty && tags.length === 0 && availableOptions.length > 0) {
        propagateChange([availableOptions[0].value])
      }
    }, [tags, allowEmpty, availableOptions, propagateChange])

    useEffect(() => {
      setAvailableValues(availableOptions.map(({ value }) => value))
    }, [availableOptions])

    // To ensure that tags are always among availableOptions because we don't
    // re-render this component automatically when availableOptions changes.
    useShallowCompareEffect(() => {
      const newTags = tags.filter((tag) => availableValues.includes(tag))

      if (!isEqual(tags, newTags)) {
        setTags(newTags)
      }
    }, [availableValues, tags, isEqual])

    useShallowCompareEffect(() => {
      setTags(inputTags)
    }, [inputTags])

    useImperativeHandle(ref, () => ({
      focus: () => {
        setIsEditing(true)
        editableFieldRef.current?.focus()
      },
      setEditing: (editingState) => {
        setIsEditing(editingState)
      },
      toggleEditing: () => {
        setIsEditing((v) => !v)
      },
    }))

    const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
      setInput(e.currentTarget.value)
    }

    const visibleOptions = useMemo(() => {
      return availableOptions.filter((option) => (option.value ?? '').toLowerCase().includes(input.toLowerCase()))
    }, [availableOptions, input])

    const [highlightedOptionIdx, setHighlightedOptionIdx] = useState(0)

    const isCreateOptionVisible = useMemo(() => {
      // Don't show create options if no onCreated was passed or you can't edit the options
      if (!onCreated || !canEditOptions) return false
      return input.length > 0 && (visibleOptions.length === 0 || !visibleOptions.map((vo) => vo.value).includes(input))
    }, [input, visibleOptions, canEditOptions, onCreated])

    useEffect(() => {
      if (visibleOptions.length === 0) {
        setHighlightedOptionIdx(0)
        return
      }
      const rangeEnd = isCreateOptionVisible ? visibleOptions.length : visibleOptions.length - 1
      setHighlightedOptionIdx(clamp(highlightedOptionIdx, 0, rangeEnd))
    }, [highlightedOptionIdx, input, isCreateOptionVisible, visibleOptions])

    const highlightedOptionName = useMemo(() => {
      if (visibleOptions.length === 0 || highlightedOptionIdx > visibleOptions.length - 1) {
        return input
      }
      return visibleOptions[highlightedOptionIdx].value
    }, [highlightedOptionIdx, visibleOptions, input])

    useKey(
      'ArrowUp',
      (e) => {
        if (e.target !== editableFieldRef.current) return
        e.stopPropagation()
        e.preventDefault() // prevent going to the start of the input
        setHighlightedOptionIdx((idx) => {
          if (visibleOptions.length === 0) return 0
          return clamp(idx - 1, 0, visibleOptions.length - 1)
        })
      },
      undefined,
      [visibleOptions],
    )
    useKey(
      'ArrowDown',
      (e) => {
        if (e.target !== editableFieldRef.current) return
        e.stopPropagation()
        e.preventDefault() // prevent going to the end of the input
        setHighlightedOptionIdx((idx) => {
          if (visibleOptions.length === 0) return 0
          const rangeEnd = isCreateOptionVisible ? visibleOptions.length : visibleOptions.length - 1
          return clamp(idx + 1, 0, rangeEnd)
        })
      },
      undefined,
      [visibleOptions, isCreateOptionVisible],
    )

    useEffect(() => {
      onEditingStateChange?.(isEditing)
    }, [isEditing, onEditingStateChange])

    const handleAddOption = (option: string) => {
      setIsEditing(false)
      if (tags.map((t) => t.toLowerCase()).includes(option.toLowerCase())) {
        setInput('')
        return
      }
      // optimistic update - necessary to see new value in input
      setAvailableValues((oldValues) => [...oldValues, option])
      if (mode === 'single') {
        propagateChange([option])
      } else {
        propagateChange(tags.concat(option))
      }
    }

    useKey(
      'Tab',
      (e) => {
        if (e.target !== editableFieldRef.current || variant !== OptionSelectVariant.TableView) return
        // close dropdown
        setInput('')
        setHighlightedOptionIdx(0)
        setIsEditing(false)
        // closing the dropdown will focus its cell, so we queue the focus change event on the next tick
        setTimeout(() => {
          document.body.dispatchEvent(
            new CustomEvent<SelectPropertyEventDetails>(selectPropertyEventName, {
              bubbles: false,
              detail: e.shiftKey ? 'Previous' : 'Next',
            }),
          )
        }, 0)
      },
      undefined,
      [variant, editableFieldRef],
    )

    useKey(
      'Enter',
      (e) => {
        if (e.target !== editableFieldRef.current) return
        const isCreatingNewOption = highlightedOptionIdx === visibleOptions.length && input.length > 0
        if (isCreatingNewOption) {
          onCreated?.(input, newColor)
        }
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        handleAddOption(isCreatingNewOption ? input : highlightedOptionName!)
        setInput('')
        resetColor()
        setHighlightedOptionIdx(0)
        document.body.dispatchEvent(
          new CustomEvent<SelectPropertyEventDetails>(selectPropertyEventName, {
            bubbles: false,
            detail: 'Down',
          }),
        )
      },
      undefined,
      [input, highlightedOptionIdx, visibleOptions, highlightedOptionName, editableFieldRef],
    )

    useKey(
      'Backspace',
      (e) => {
        if (input.length === 0 && e.target === editableFieldRef.current && canRemove) {
          propagateChange(tags.slice(0, -1))
        }
      },
      undefined,
      [input, tags],
    )

    const handleRemoveTag = (index: number) => () => {
      propagateChange(tags.filter((_, i) => i !== index))
    }
    const handleEditableListClick = (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === editableTagListRef.current) {
        editableFieldRef.current?.focus()
      }
    }

    const handleTagOptionClick = (option: PropertyValue) => () => {
      handleAddOption(option.value)
      setInput('')
      setHighlightedOptionIdx(0)
    }

    const overlayRef = useRef<HTMLDivElement>(null)

    const { shouldFlipOverlayPlacement, positionPropsStyle } = useOverlayHack(
      overlayRef,
      selectedTagsButtonRef,
      isEditing,
      buttonHeight,
      14,
    )

    const paddedButtonHeight = buttonHeight + 16

    return (
      <div
        className={cx({
          [styles.OptionSelect]: variant === OptionSelectVariant.TableView,
          [styles.OptionSelectFilter]: variant === OptionSelectVariant.Filter,
        })}
      >
        <OptionButton
          options={availableOptions}
          tags={tags}
          ref={selectedTagsButtonRef}
          onPress={() => {
            if (isEditable) {
              setIsEditing(true)
            }
          }}
          endIcon={variant === OptionSelectVariant.Filter ? ChevronDown : undefined}
          optionVariant={variant}
          isDisabled={!isEditable}
        />
        {isEditing && (
          <FloatingPortal id="floating-ui-root">
            <Popover
              style={positionPropsStyle}
              isOpen={true}
              onClose={() => setIsEditing(false)}
              ref={overlayRef}
              isDismissable
              className={cx(styles.popover, { [styles.flipped]: shouldFlipOverlayPlacement })}
            >
              <div
                className={cx({
                  [styles.editableTagList]: variant === OptionSelectVariant.TableView,
                  [styles.editableTagListFilter]: variant === OptionSelectVariant.Filter,
                })}
                ref={editableTagListRef}
                onClick={handleEditableListClick}
                style={{ width: buttonWidth + 16, minHeight: paddedButtonHeight }}
                data-testid="options-select-selected-options"
              >
                {tags.map((tag, index) => (
                  <Tag
                    key={index}
                    color={availableOptions.find((opt) => opt.value === tag)?.meta?.color ?? ''}
                    onRemove={canRemove ? handleRemoveTag(index) : undefined}
                  >
                    {tag}
                  </Tag>
                ))}
                <div className={styles.sizer} ref={inputSizerRef} aria-hidden={true}>
                  {input}
                </div>
                <input
                  type="text"
                  className={styles.editableField}
                  value={input}
                  onChange={handleInput}
                  ref={editableFieldRef}
                  style={{ width: inputWidth + 30 }}
                  autoFocus
                />
              </div>
              <div
                className={styles.dropdown}
                style={{ maxWidth: Math.max(208, buttonWidth + 16) }}
                data-testid="option-select-available-options"
              >
                {onCreated && (
                  <div className={styles.title}>
                    {(!visibleOptions.length && canEditOptions && 'Type to create a new option') ||
                      (canEditOptions && 'Select an option or create one') ||
                      'Select an option'}
                  </div>
                )}
                <ul className={styles.visibleOptions}>
                  {visibleOptions.map((option, idx) => (
                    <li
                      key={idx}
                      className={cx(styles.option, {
                        [styles.highlighted]: idx === highlightedOptionIdx,
                      })}
                      onClick={handleTagOptionClick(option)}
                    >
                      <Tag color={option.meta?.color ?? ''}>{option.value}</Tag>
                      {canEditOptions && (
                        <OptionEdit propertyValue={option} onUpdated={onUpdated} onDeleted={onDeleted} />
                      )}
                    </li>
                  ))}
                  {isCreateOptionVisible && (
                    <li
                      className={cx(styles.option, styles.createOption, {
                        [styles.highlighted]: highlightedOptionIdx === visibleOptions.length,
                      })}
                      onClick={() => {
                        resetColor()
                        onCreated?.(input, newColor)
                        handleAddOption(input)
                        setInput('')
                      }}
                    >
                      <AddIcon />
                      <div className={styles.createText}>Create</div>
                      <Tag color={newColor ?? ''}>{input}</Tag>
                    </li>
                  )}
                </ul>
              </div>
            </Popover>
          </FloatingPortal>
        )}
      </div>
    )
  },
)
OptionSelect.displayName = 'OptionSelect'
